import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Table,
    Badge,
    Spinner,
    Alert,
    Button,
    Input,
    Label,
    FormGroup,
    InputGroup,
    InputGroupText,
    Pagination,
    PaginationItem,
    PaginationLink,
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";

interface StockMovement {
    rowid: number;
    datem: string;
    qty: number;
    label: string;
    inventorycode: string;
    price: number;
    type_mouvement: number;
    origintype: string | null;
    fk_origin: number | null;
    batch: string | null;
    product_id: number | null;
    product_ref: string | null;
    product_label: string | null;
    product_barcode: string | null;
    entrepot_id: number;
    entrepot_ref: string;
    user_id: number | null;
    user_login: string | null;
    user_firstname: string | null;
    user_lastname: string | null;
}

interface Entrepot {
    id: number;
    ref: string;
    lieu: string;
}

interface MouvementsResponse {
    success: boolean;
    message: string;
    data: StockMovement[];
    nbtotalofrecords: number;
    totalpages: number;
    current_page: number;
    count: number;
}

interface WarehousesResponse {
    success: boolean;
    data: Entrepot[];
}

const API_URL = "https://api.apapiecesauto.fr/apiV2/";
const PAGE_LIMIT = 50;

const formatDate = (d: string) => {
    if (!d) return "-";
    const date = new Date(d.replace(" ", "T"));
    if (isNaN(date.getTime())) return d;
    return date.toLocaleString("fr-FR");
};

const Mouvements = () => {
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [movements, setMovements] = useState<StockMovement[]>([]);
    const [entrepots, setEntrepots] = useState<Entrepot[]>([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [searchProductRef, setSearchProductRef] = useState("");
    const [searchBarcode, setSearchBarcode] = useState("");
    const [searchEntrepot, setSearchEntrepot] = useState("");

    const fetchWarehouses = async () => {
        try {
            const response: any = await axios.post<WarehousesResponse>(
                API_URL,
                {
                    action: "mouvements",
                    sub_action: "warehouses",
                    entity: user.entity,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );
            if (response.success) {
                setEntrepots(response.data || []);
            }
        } catch (e) {
            // silencieux, le filtre entrepôt reste vide
        }
    };

    const fetchMouvements = async (resetPage = false) => {
        try {
            setLoading(true);
            setError(null);

            const targetPage = resetPage ? 1 : page;
            if (resetPage) setPage(1);

            const response: any = await axios.post<MouvementsResponse>(
                API_URL,
                {
                    action: "mouvements",
                    sub_action: "list",
                    entity: user.entity,
                    search_product_ref: searchProductRef.trim(),
                    search_barcode: searchBarcode.trim(),
                    search_entrepot: searchEntrepot ? Number(searchEntrepot) : 0,
                    page: targetPage,
                    limit: PAGE_LIMIT,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            const d = response;
            if (d.success) {
                setMovements(d.data || []);
                setTotalPages(d.totalpages || 1);
                setTotalCount(d.nbtotalofrecords || 0);
            } else {
                throw new Error(d.message || "Erreur de chargement");
            }
        } catch (e: any) {
            setError(e.message || "Erreur lors du chargement");
            setMovements([]);
            setTotalPages(1);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchWarehouses();
            fetchMouvements(true);
        }
    }, [user?.token, user?.entity]);

    useEffect(() => {
        if (user?.token) fetchMouvements(false);
    }, [page]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchMouvements(true);
    };

    const resetFilters = () => {
        setSearchProductRef("");
        setSearchBarcode("");
        setSearchEntrepot("");
        setTimeout(() => fetchMouvements(true), 0);
    };

    const qtyBadge = (q: number) =>
        q >= 0 ? <Badge color="success">+{q}</Badge> : <Badge color="danger">{q}</Badge>;

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const items: JSX.Element[] = [];
        const maxButtons = 7;
        const start = Math.max(1, page - Math.floor(maxButtons / 2));
        const end = Math.min(totalPages, start + maxButtons - 1);

        items.push(
            <PaginationItem key="prev" disabled={page <= 1}>
                <PaginationLink previous onClick={() => setPage(page - 1)} />
            </PaginationItem>
        );
        for (let i = start; i <= end; i++) {
            items.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }
        items.push(
            <PaginationItem key="next" disabled={page >= totalPages}>
                <PaginationLink next onClick={() => setPage(page + 1)} />
            </PaginationItem>
        );
        return <Pagination className="mb-0">{items}</Pagination>;
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Row className="mb-3">
                    <Col>
                        <h4 className="mb-0">Mouvements de stock</h4>
                        <small className="text-muted">
                            {totalCount} mouvement(s) — filtrés sur vos entrepôts
                        </small>
                    </Col>
                </Row>

                <Card className="mb-3">
                    <CardBody>
                        <form onSubmit={handleSearch}>
                            <Row className="g-2 align-items-end">
                                <Col md={3}>
                                    <FormGroup className="mb-0">
                                        <Label className="form-label small">Référence produit</Label>
                                        <InputGroup>
                                            <InputGroupText><i className="ri-search-line"></i></InputGroupText>
                                            <Input
                                                type="text"
                                                value={searchProductRef}
                                                onChange={(e) => setSearchProductRef(e.target.value)}
                                                placeholder="Ex: REF123"
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-0">
                                        <Label className="form-label small">Code-barres</Label>
                                        <InputGroup>
                                            <InputGroupText><i className="ri-barcode-line"></i></InputGroupText>
                                            <Input
                                                type="text"
                                                value={searchBarcode}
                                                onChange={(e) => setSearchBarcode(e.target.value)}
                                                placeholder="EAN / batch"
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-0">
                                        <Label className="form-label small">Entrepôt</Label>
                                        <Input
                                            type="select"
                                            value={searchEntrepot}
                                            onChange={(e) => setSearchEntrepot(e.target.value)}
                                        >
                                            <option value="">Tous</option>
                                            {entrepots.map((e) => (
                                                <option key={e.id} value={e.id}>
                                                    {e.ref}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={3} className="d-flex gap-2">
                                    <Button color="primary" type="submit" disabled={loading}>
                                        <i className="ri-filter-3-line align-bottom me-1"></i>
                                        Rechercher
                                    </Button>
                                    <Button color="light" type="button" onClick={resetFilters} disabled={loading}>
                                        <i className="ri-refresh-line align-bottom me-1"></i>
                                        Réinitialiser
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </CardBody>
                </Card>

                {error && <Alert color="danger">{error}</Alert>}

                <Card>
                    <CardHeader>
                        <i className="ri-exchange-line align-bottom me-2"></i>
                        Liste des mouvements
                    </CardHeader>
                    <CardBody className="p-0">
                        <div className="table-responsive">
                            <Table className="align-middle mb-0" hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Entrepôt</th>
                                        <th>Produit</th>
                                        <th>Code-barres</th>
                                        <th className="text-end">Qté</th>
                                        <th>Libellé</th>
                                        <th>Utilisateur</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-4">
                                                <Spinner size="sm" /> Chargement...
                                            </td>
                                        </tr>
                                    ) : movements.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-4">
                                                Aucun mouvement trouvé
                                            </td>
                                        </tr>
                                    ) : (
                                        movements.map((m) => (
                                            <tr key={m.rowid}>
                                                <td>{formatDate(m.datem)}</td>
                                                <td>{m.entrepot_ref}</td>
                                                <td>
                                                    <div><strong>{m.product_ref || "-"}</strong></div>
                                                    <small className="text-muted">{m.product_label}</small>
                                                </td>
                                                <td>{m.product_barcode || m.batch || "-"}</td>
                                                <td className="text-end">{qtyBadge(m.qty)}</td>
                                                <td>{m.label || "-"}</td>
                                                <td>
                                                    {m.user_firstname || m.user_lastname
                                                        ? `${m.user_firstname ?? ""} ${m.user_lastname ?? ""}`.trim()
                                                        : m.user_login || "-"}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                            Page {page} / {totalPages} — {totalCount} résultats
                        </small>
                        {renderPagination()}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default Mouvements;
